import React, { useState, useEffect, useRef } from 'react';
import { Input, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';


const { Text } = Typography;

const navigationItems = [
  { emoji: '🏷️', label: 'Ready Products', path: '/dashboard/masters/ready-products', shortcut: 'Ctrl+1' },
  { emoji: '📦', label: 'Raw Materials', path: '/dashboard/masters/raw-materials', shortcut: 'Ctrl+2' },
  { emoji: '🔢', label: 'Unit Master', path: '/dashboard/masters/units', shortcut: '' },
  { emoji: '🔗', label: 'Raw vs Ready', path: '/dashboard/masters/mappings', shortcut: 'Ctrl+3' },
  { emoji: '🏪', label: 'Vendors', path: '/dashboard/masters/vendors', shortcut: 'Ctrl+4' },
  { emoji: '🛒', label: 'Raw Purchase', path: '/dashboard/transactions/purchase', shortcut: 'Ctrl+5' },
  { emoji: '⚙️', label: 'Production Entry', path: '/dashboard/transactions/production', shortcut: 'Ctrl+6' },
  { emoji: '📊', label: 'Stock Summary', path: '/dashboard/reports/stock', shortcut: '' },
  { emoji: '👥', label: 'Users', path: '/dashboard/admin/users', shortcut: 'Ctrl+8' },
];

const quickActions = [
  { emoji: '➕', label: 'Add New Record', shortcut: 'Ctrl+N', action: 'add' },
  { emoji: '🔄', label: 'Refresh Data', shortcut: 'Ctrl+R', action: 'refresh' },
  { emoji: '🖨️', label: 'Print', shortcut: 'Ctrl+P', action: 'print' },
  { emoji: '📥', label: 'Export CSV', shortcut: 'Ctrl+E', action: 'export' },
];

const CommandPalette = ({ isOpen, onClose, currentPageActions = [] }) => {
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const filteredNav = navigationItems.filter(item =>
    item.label.toLowerCase().includes(search.toLowerCase())
  );

  const filteredActions = [...quickActions, ...currentPageActions].filter(item =>
    item.label.toLowerCase().includes(search.toLowerCase())
  );

  const allItems = [
    ...filteredNav.map(item => ({ ...item, type: 'nav' })),
    ...filteredActions.map(item => ({ ...item, type: 'action' }))
  ];

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setHighlightedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [search]);

  const handleSelect = (item) => {
    if (item.type === 'nav') {
      navigate(item.path);
    } else {
      // Trigger action - parent component should handle via currentPageActions
      if (item.action) {
        item.onAction?.();
      }
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(15, 23, 42, 0.6)',
        backdropFilter: 'blur(4px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '15vh'
      }}
      onClick={onClose}
    >
      <div
        style={{
          maxWidth: 560,
          width: '90%',
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
          overflow: 'hidden'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Input */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
          <Input
            ref={inputRef}
            prefix={<SearchOutlined style={{ color: '#64748b', fontSize: 18 }} />}
            placeholder="Search commands..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            bordered={false}
            size="large"
            style={{ fontSize: 16 }}
          />
        </div>

        {/* Results */}
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          {/* Navigation Section */}
          {filteredNav.length > 0 && (
            <div>
              <div style={{ padding: '12px 20px 8px', background: '#f8fafc' }}>
                <Text style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  NAVIGATE TO
                </Text>
              </div>
              {filteredNav.map((item, idx) => {
                const globalIdx = idx;
                const isHighlighted = highlightedIndex === globalIdx;
                return (
                  <div
                    key={idx}
                    onClick={() => handleSelect({ ...item, type: 'nav' })}
                    style={{
                      padding: '12px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      background: isHighlighted ? '#eff6ff' : 'transparent',
                      borderLeft: isHighlighted ? '2px solid #1a56db' : '2px solid transparent',
                      transition: 'all 0.15s'
                    }}
                    onMouseEnter={() => setHighlightedIndex(globalIdx)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontSize: 18 }}>{item.emoji}</span>
                      <Text style={{ fontSize: 14 }}>{item.label}</Text>
                    </div>
                    {item.shortcut && (
                      <Text style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>
                        {item.shortcut}
                      </Text>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Quick Actions Section */}
          {filteredActions.length > 0 && (
            <div>
              <div style={{ padding: '12px 20px 8px', background: '#f8fafc' }}>
                <Text style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  QUICK ACTIONS
                </Text>
              </div>
              {filteredActions.map((item, idx) => {
                const globalIdx = filteredNav.length + idx;
                const isHighlighted = highlightedIndex === globalIdx;
                return (
                  <div
                    key={idx}
                    onClick={() => handleSelect({ ...item, type: 'action' })}
                    style={{
                      padding: '12px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      background: isHighlighted ? '#eff6ff' : 'transparent',
                      borderLeft: isHighlighted ? '2px solid #1a56db' : '2px solid transparent',
                      transition: 'all 0.15s'
                    }}
                    onMouseEnter={() => setHighlightedIndex(globalIdx)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{ fontSize: 18 }}>{item.emoji}</span>
                      <Text style={{ fontSize: 14 }}>{item.label}</Text>
                    </div>
                    {item.shortcut && (
                      <Text style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>
                        {item.shortcut}
                      </Text>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* No Results */}
          {allItems.length === 0 && (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <Text style={{ color: '#94a3b8' }}>No results found</Text>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
